import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { spacing } from 'ui/theme';
import { InputText } from 'ui/inputs';
import { Undo16 } from 'ui/icons';
import { useFormContext, Controller } from 'react-hook-form';
import { ConnectedFormField } from 'modules/projectSettings/components/ProjectSettingsForm/ConnectedFormField';
import { BUTTON_SIZES, GhostSuccessButton, GhostButton } from 'ui/button';
import { CONTENT_FIELD_NAMES } from 'modules/projectSettings/constants';
import { ProjectSettingsFormSchemaType } from 'modules/projectSettings/contexts';
import { maskHMACSHA1 } from 'modules/projectSettings/utils';

const FieldsWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 0.5fr;
  gap: ${spacing.base};
`;

const { HMAC_SECRET } = CONTENT_FIELD_NAMES;

const HMAC_FORM_FIELD_TEXT = {
  undoButtonText: 'Undo changes',
  clearButtonText: 'Clear HMAC secret',
};

export interface HMACFormFieldProps {
  fieldDescriptionText: ReactNode;
  fieldLabelText: ReactNode;
}
export const HMACFormField: FC<HMACFormFieldProps> = ({ fieldDescriptionText, fieldLabelText }) => {
  const { setValue, resetField, getFieldState, watch } =
    useFormContext<ProjectSettingsFormSchemaType>();
  const hmacField = watch(HMAC_SECRET);
  const { isDirty } = getFieldState(HMAC_SECRET);
  const isFieldSet = !!hmacField && !isDirty;

  const handleClear = () => {
    setValue(HMAC_SECRET, '', { shouldDirty: true });
  };
  const handleUndo = () => {
    resetField(HMAC_SECRET);
  };

  let buttonContent = match({
    hmacField: !!hmacField,
    isDirty,
  })
    .with(
      {
        hmacField: false,
        isDirty: false,
      },
      () => {
        return (
          <GhostButton
            onClick={handleUndo}
            isDisabled
            size={BUTTON_SIZES.LARGE}
            data-test={`hmac-undo-button`}
          >
            <Undo16 />
            {HMAC_FORM_FIELD_TEXT.undoButtonText}
          </GhostButton>
        );
      },
    )
    .with(
      {
        hmacField: false,
        isDirty: true,
      },
      () => {
        return (
          <GhostButton
            onClick={handleUndo}
            size={BUTTON_SIZES.LARGE}
            data-test={`hmac-undo-button`}
          >
            <Undo16 />
            {HMAC_FORM_FIELD_TEXT.undoButtonText}
          </GhostButton>
        );
      },
    )
    .with(
      {
        hmacField: true,
        isDirty: false,
      },
      () => {
        return (
          <GhostSuccessButton
            onClick={handleClear}
            size={BUTTON_SIZES.LARGE}
            data-test={`hmac-clear-button`}
          >
            {HMAC_FORM_FIELD_TEXT.clearButtonText}
          </GhostSuccessButton>
        );
      },
    )
    .with(
      {
        hmacField: true,
        isDirty: true,
      },
      () => {
        return (
          <GhostButton
            onClick={handleUndo}
            size={BUTTON_SIZES.LARGE}
            data-test={`hmac-undo-button`}
          >
            <Undo16 />
            {HMAC_FORM_FIELD_TEXT.undoButtonText}
          </GhostButton>
        );
      },
    )
    .exhaustive();

  return (
    <ConnectedFormField
      name={HMAC_SECRET}
      descriptionText={fieldDescriptionText}
      labelText={fieldLabelText}
    >
      <FieldsWrapper>
        <Controller
          name={HMAC_SECRET}
          render={({ field, fieldState }) => {
            return (
              <InputText
                {...field}
                {...fieldState}
                isDisabled={isFieldSet}
                value={isFieldSet ? maskHMACSHA1(field.value) : field.value}
              />
            );
          }}
        />
        {buttonContent}
      </FieldsWrapper>
    </ConnectedFormField>
  );
};
